import React, { useState } from 'react';
import { string } from 'prop-types';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { StringConstantsMixin } from 'react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from 'utils/language_support_util';
import { retrievePrintedGift } from 'utils/requests/gifting_requests';
import PrimaryButtonLarge from '@alltrails/shared/components/PrimaryButtonLarge';
import { FormattedMessage } from 'react-intl';
import * as styles from './styles/styles.module.scss';
import GiftCard from '../GiftCard';
import ProgressDialog from '../../shared/ProgressDialog';

const {
  USE_YOUR,
  GIFT_FLOW: {
    PRINT: { NEW_TO_ALLTRAILS, ALREADY_HAVE_ONE, REDEMPTION_CODE, YOUR_GIFT_SUBSCRIPTION, ANNUAL_PRO_PLAN, THREE_YEAR_PRO_PLAN },
    GIFT_CUSTOMIZE_FORM: { TO, FROM, PRINT }
  }
} = StringConstantsMixin.strings;

const NameAndMessage = ({ message, senderName, recipientName }) => (
  <>
    <div className={styles.senderRecipient}>
      <span>{TO}:</span> {recipientName}
    </div>
    <div className={styles.giftMessage}>{message}</div>
    <div className={styles.senderRecipient}>
      <span>{FROM}: </span> {senderName}
    </div>
  </>
);

const GiftCardPrint = ({ id, code, planType, recipientName, senderName, message, background }) => {
  const languageRegionCode = useLanguageRegionCode();
  const [printingState, updatePrintingState] = useState(null);

  const handlePrintSuccess = data => {
    const downloadLink = document.createElement('a');
    downloadLink.href = data.download_url;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    updatePrintingState(null);
  };

  const printGiftCard = () => {
    updatePrintingState('loading');
    retrievePrintedGift(id, languageRegionCode)
      .then(handlePrintSuccess)
      .catch(() => updatePrintingState('error'));
  };

  const shortRegionCode = languageRegionCode === 'en-US' ? '' : `/${languageRegionCode.split('-')[0]}`;

  const signUpText = (
    <>
      <span className={styles.bold}>{NEW_TO_ALLTRAILS} </span>
      <FormattedMessage
        defaultMessage="Go to <a></a> to create an account"
        values={{
          a: () => <a href={LanguageSupportUtil.wrapUrlSafe('/signup', languageRegionCode)}>{`www.alltrails.com${shortRegionCode}/signup`}</a>
        }}
      />
    </>
  );

  const redeemText = (
    <>
      <span className={styles.bold}>{ALREADY_HAVE_ONE} </span>
      <FormattedMessage
        defaultMessage="Use your redemption code at <a></a>"
        values={{
          a: () => (
            <a href={LanguageSupportUtil.wrapUrlSafe(`/redeem?k=${code}`, languageRegionCode)}>{`www.alltrails.com${shortRegionCode}/redeem`}</a>
          )
        }}
      />
    </>
  );

  return (
    <div className={styles.giftCardPrint}>
      <div className={styles.printContainer}>
        <div className={styles.cardAndData}>
          <div className={styles.cardContainer}>
            <div className={styles.card}>
              <GiftCard backgroundId={background} classes={styles.giftCardCard} />
              <div className={styles.inMobile}>
                <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
              </div>
            </div>
            <div className={styles.cardMeta}>
              <div className={styles.planType}>{planType === 'annual' ? ANNUAL_PRO_PLAN : THREE_YEAR_PRO_PLAN}</div>
              <div className={styles.redemptionCode}>
                {REDEMPTION_CODE}: <span>{code}</span>
              </div>
            </div>
          </div>
          <div className={styles.metadata}>
            <div className={styles.yourGiftCard}>{YOUR_GIFT_SUBSCRIPTION}</div>
            <div className={styles.desktop}>
              <NameAndMessage message={message} senderName={senderName} recipientName={recipientName} />
            </div>
          </div>
        </div>
        <div className={styles.subTextContainer}>
          <div className={styles.subText}>{signUpText}</div>
          <div className={styles.subText}>{redeemText}</div>
        </div>
      </div>
      <div className={styles.printButton}>
        <PrimaryButtonLarge onClick={printGiftCard}>{PRINT}</PrimaryButtonLarge>
      </div>
      {printingState && <ProgressDialog variant={printingState} updateProgressDialog={updatePrintingState} />}
    </div>
  );
};

GiftCardPrint.propTypes = {
  code: string.isRequired,
  planType: string.isRequired,
  senderName: string.isRequired,
  recipientName: string.isRequired,
  message: string.isRequired,
  background: string.isRequired
};

export default GiftCardPrint;
